import { memo } from "react";
import { ChevronRight, ChevronDown, MessageSquare } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import type { Project } from "@/lib/api";

export type SortableColumn =
  | "client" | "project" | "start_date" | "internal_due_date" | "due_date" | "next_task_due_date";

export interface ProjectsTableGroup {
  key: string;
  label: string;
  openCount: number;
  totalCount: number;
  projects: Project[];
}

export interface ProjectsTableProps {
  /** Flat list (already filtered + sorted). Used when `groups` is undefined. */
  projects: Project[];
  /** When provided, render collapsible grouped sections instead of a flat list. */
  groups?: ProjectsTableGroup[];
  expandedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  sortColumn: SortableColumn | null;
  sortDir: "asc" | "desc";
  onSortColumn: (col: SortableColumn) => void;
  onOpenProject: (p: Project) => void;
  onOpenChat: (p: Project) => void;
  renderAssignees: (p: Project) => React.ReactNode;
  renderTags: (p: Project) => React.ReactNode;
  renderWorkflowBadge: (p: Project) => React.ReactNode;
}

function fmtHrs(min?: number): string {
  return `${((min ?? 0) / 60).toFixed(1)} hrs`;
}
function fmtDate(d?: string | null): string {
  if (!d) return "—";
  const dt = new Date(`${d}T12:00:00`); // noon to avoid TZ shift (project convention)
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function isOverdue(d?: string | null): boolean {
  if (!d) return false;
  return new Date(`${d}T12:00:00`) < new Date(new Date().toDateString());
}

const COLS: { key: SortableColumn | null; label: string; sortable: boolean; className?: string }[] = [
  // client is pinned, so we absorb all remaining space
  { key: "client", label: "Client", sortable: true, className: "w-56" },
  { key: "project", label: "Project", sortable: true },
  { key: null, label: "Team Chat", sortable: false, className: "text-center w-24" },
  { key: null, label: "Client Tasks", sortable: false, className: "text-center w-28" },
  { key: null, label: "Actual/Budget", sortable: false, className: "text-center w-32" },
  { key: "start_date", label: "Start", sortable: true, className: "w-24" },
  { key: "internal_due_date", label: "Internal Due", sortable: true, className: "w-28" },
  { key: "due_date", label: "Due", sortable: true, className: "w-24" },
  { key: "next_task_due_date", label: "Next Task Due", sortable: true, className: "w-28" },
  { key: null, label: "Assignee", sortable: false, className: "w-28" },
];

// only when actually needed, since rows are memoized and must not recieve the whole props object
type RowRenderers = Pick<
  ProjectsTableProps,
  "onOpenProject" | "onOpenChat" | "renderAssignees" | "renderTags" | "renderWorkflowBadge"
>;

export function ProjectsTable(props: ProjectsTableProps) {
  const { projects, groups, expandedGroups, onToggleGroup, sortColumn, sortDir, onSortColumn } = props;
  const renderers: RowRenderers = {
    onOpenProject: props.onOpenProject,
    onOpenChat: props.onOpenChat,
    renderAssignees: props.renderAssignees,
    renderTags: props.renderTags,
    renderWorkflowBadge: props.renderWorkflowBadge,
  };

  return (
    <div className="w-full overflow-x-auto rounded-md border">
      {/* fixed layout reads off header row instead */}
      <table className="w-full min-w-[1400px] table-fixed text-sm">
        <thead className="bg-muted/40 sticky top-0">
          <tr className="border-b">
            {COLS.map((c, i) => (
              <th
                key={i}
                className={`px-3 py-2 text-left font-semibold text-muted-foreground select-none ${c.className ?? ""} ${c.sortable ? "cursor-pointer" : ""}`}
                onClick={() => c.sortable && c.key && onSortColumn(c.key)}
              >
                {c.label}
                {c.sortable && c.key === sortColumn ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups
            ? groups.map((g) => (
                <GroupSection
                  key={g.key}
                  group={g}
                  open={expandedGroups.has(g.key)}
                  onToggleGroup={onToggleGroup}
                  {...renderers}
                />
              ))
            : projects.map((p) => <ProjectRowCells key={p.id} project={p} {...renderers} />)}
        </tbody>
      </table>
    </div>
  );
}

const GroupSection = memo(function GroupSection(
  props: RowRenderers & {
    group: ProjectsTableGroup;
    open: boolean;
    onToggleGroup: (key: string) => void;
  },
) {
  const { group, open, onToggleGroup, ...renderers } = props;
  return (
    <>
      <tr className="bg-muted/20 border-b cursor-pointer" onClick={() => onToggleGroup(group.key)}>
        <td colSpan={10} className="px-3 py-2 font-semibold">
          <span className="inline-flex items-center gap-2">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {group.label}
            <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {group.openCount} open
            </span>
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {group.totalCount} total
            </span>
          </span>
        </td>
      </tr>
      {open && group.projects.map((p) => <ProjectRowCells key={p.id} project={p} {...renderers} indent />)}
    </>
  );
});

const ProjectRowCells = memo(function ProjectRowCells(
  props: RowRenderers & { project: Project; indent?: boolean },
) {
  const { project: p, onOpenProject, onOpenChat, renderAssignees, renderTags, renderWorkflowBadge, indent } = props;
  const firmPct = p.firm_task_total ? Math.round(((p.firm_task_done ?? 0) / p.firm_task_total) * 100) : 0;
  return (
    <tr className="border-b hover:bg-muted/30">
      {/* CLIENT — opens the exact client + this project */}
      <td className={`px-3 py-2 font-medium ${indent ? "pl-8" : ""}`}>
        {p.account_name ? (
          <a
            href={`/accounts/${p.account_id}?openProject=${p.id}`}
            className="text-left text-primary hover:underline"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              onOpenProject(p);
            }}
          >
            {p.account_name}
          </a>
        ) : "—"}
      </td>
      {/* PROJECT (title + chips + progress bar) */}
      <td className="px-3 py-2">
        <a
          href={`/accounts/${p.account_id}?openProject=${p.id}`}
          className="block text-left font-medium hover:underline"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            onOpenProject(p);
          }}
        >
          {p.title}
        </a>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {p.period_label && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{p.period_label}</span>}
          {renderWorkflowBadge(p)}
          {renderTags(p)}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <Progress value={firmPct} className="h-1.5 w-40" />
          <span className="text-xs text-muted-foreground">{p.firm_task_done ?? 0}/{p.firm_task_total ?? 0}</span>
        </div>
      </td>
      {/* TEAM CHAT — opens this project's team chat */}
      <td className="px-3 py-2 text-center">
        <button
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
          onClick={() => onOpenChat(p)}
          title="Open team chat"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          {(p.comment_count ?? 0) > 0 ? p.comment_count : ""}
        </button>
      </td>
      {/* CLIENT TASKS — click the ratio to open the project at its client tasks */}
      <td className="px-3 py-2 text-center">
        {(p.client_task_total ?? 0) > 0 ? (
          <button
            className="text-primary hover:underline font-medium"
            onClick={() => onOpenProject(p)}
            title="Open client tasks in this project"
          >
            {p.client_task_done ?? 0}/{p.client_task_total ?? 0}
          </button>
        ) : (
          <span className="text-muted-foreground">{p.client_task_done ?? 0}/{p.client_task_total ?? 0}</span>
        )}
      </td>
      {/* ACTUAL/BUDGET */}
      <td className="px-3 py-2 text-center text-muted-foreground">{fmtHrs(p.actual_minutes)} / {fmtHrs(p.budget_minutes)}</td>
      {/* START */}
      <td className="px-3 py-2">{fmtDate(p.start_date)}</td>
      {/* INTERNAL DUE */}
      <td className="px-3 py-2">{fmtDate(p.internal_due_date)}</td>
      {/* DUE (with status dot) */}
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${isOverdue(p.due_date) ? "bg-rose-500" : "bg-amber-400"}`} />
          <span className={isOverdue(p.due_date) ? "text-rose-600" : ""}>{fmtDate(p.due_date)}</span>
        </span>
      </td>
      {/* NEXT TASK DUE */}
      <td className="px-3 py-2">{fmtDate(p.next_task_due_date)}</td>
      {/* ASSIGNEES */}
      <td className="px-3 py-2">{renderAssignees(p)}</td>
    </tr>
  );
});
